import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { CreateCustomerWrapper } from "./styled";
import { BaseButton } from "../../../../components/button/styled";
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import { AppContext } from "../../../../context/appContext";
import { OnboardCustomerAccount } from "../../../../containers/form/onboardcustomeraccount";
import { OnboardGroupAccount } from "../../../../containers/form/onboardgroupaccount";
import Cookies from "universal-cookie";
import { OnboardCustomerAccountFormFieldsType, OnboardGroupAccountFormFieldsType } from "../../../../types/container.type";
import { onboardCustomerService } from "../../../../utils/api/customerservice/customerlist/onboardCustomer";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { onboardGroupService } from "../../../../utils/api/customerservice/grouplist/onboardGroup";

export const CreateCustomer = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [activeTab, setActiveTab] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [customerDetails, setCustomerDetails] = useState<OnboardCustomerAccountFormFieldsType>({
        firstName: "",
        middleName: "",
        lastName: "",
        BVN: "",
        email: "",
        gender: " ",
        dob: "",
        occupation: "",
        address: "",
        country: "",
        state: " ",
        lga: " ",
        phone: "",
        branch: " ",
        accountManager: " ",
        status: " ",
        validId: "",
        customerLevel: " ",
        activateEmail: false,
        activateSMS: false,
        autoChargeSMS: false,
        allowWithdrawal: false,
        passport: "",
        signature: "",
        nextOfKinFirstName: "",
        nextOfKinMiddleName: "",
        nextOfKinLastName: "",
        nextOfKinRelationship: " ",
        nextOfKinPhone: "",
        nextOfKinEmail: "",
        nextOfKinGender: " ",
        nextOfKinOccupation: "",
        nextOfKinResidentialAddress: "",
        savingsProduct: " ",
        tier: " ",
        group: " ",
    });
    const [groupDetails, setGroupDetails] = useState<OnboardGroupAccountFormFieldsType>({
        accountType: " ",
        branch: "",
        name: "",
        registrationType: " ",
        savingsProduct: " ",
        accountManager: " ",
        meetingDay: "",
        meetingTime: "",
        meetingFrequency: " ",
        meetingLocation: "",
        email: "",
        primaryContactName: " ",
        primaryContactPhone: "",
        status: " ",
        mandate: "",
        members: []
    });

    useEffect(() => {
        setActiveNavItem("Customer Service");
    }, [setActiveNavItem]);

    const handleActiveTabUpdate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, tabId: number) => {
        e.preventDefault();
        return setActiveTab(tabId);
    };

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        const formData = new FormData();
        const data = {
            firstName: customerDetails.firstName,
            middleName: customerDetails.middleName,
            lastName: customerDetails.lastName,
            email: customerDetails.email,
            phone: customerDetails.phone,
            BVN: customerDetails.BVN,
            gender: customerDetails.gender,
            dob: customerDetails.dob,
            occupation: customerDetails.occupation,
            address: customerDetails.address,
            country: customerDetails.country,
            state: customerDetails.state,
            lga: customerDetails.lga,
            branch: customerDetails.branch,
            accountManager: customerDetails.accountManager,
            status: customerDetails.status,
            validId: customerDetails.validId,
            customerLevel: customerDetails.customerLevel,
            activateEmail: customerDetails.activateEmail,
            activateSMS: customerDetails.activateSMS,
            autoChargeSMS: customerDetails.autoChargeSMS,
            allowWithdrawal: customerDetails.allowWithdrawal,
            passport: customerDetails.passport,
            signature: customerDetails.signature,
            nextOfKinFirstName: customerDetails.nextOfKinFirstName,
            nextOfKinMiddleName: customerDetails.nextOfKinMiddleName,
            nextOfKinLastName: customerDetails.nextOfKinLastName,
            nextOfKinRelationship: customerDetails.nextOfKinRelationship,
            nextOfKinPhone: customerDetails.nextOfKinPhone,
            nextOfKinEmail: customerDetails.nextOfKinEmail,
            nextOfKinGender: customerDetails.nextOfKinGender,
            nextOfKinOccupation: customerDetails.nextOfKinOccupation,
            nextOfKinResidentialAddress: customerDetails.nextOfKinResidentialAddress,
            savingsProduct: customerDetails.savingsProduct,
            tier: customerDetails.tier,
            group: customerDetails.group,
        };
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value as any);
        });
        try {
            let response;
            activeTab === 1 ?
                response = await onboardCustomerService(TOKEN, formData)
                : response = await onboardGroupService(TOKEN, groupDetails)
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError(`Failed to onboard ${activeTab === 1 ? "customer" : "group"} account. Please check credentials and try again.`);
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`${activeTab === 1 ? "Customer" : "Group"} account onboarding failed. ${error.message}`);
            console.error(`${activeTab === 1 ? "Customer" : "Group"} account onboarding failed:`, error);
        }
    };

    return (
        <AppLayout
            pageId={"Customer-List"}
            pageTitle={"Customer List"}
        >
            <CreateCustomerWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title={`Add ${activeTab === 1 ? "Customer Account" : "Group Account"} Successful`}
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Customer List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={700}
                        fontSize={20}
                        lineHeight={"normal"}
                        color="var(--dark-color)"
                    >
                        Choose an account type.
                    </Typography>
                </Box>
                <Stack
                    gap={{ mobile: "calc(var(--flex-gap)/4)", miniTablet: "calc(var(--flex-gap)/2)" }}
                    direction={{ miniTablet: "row" }}
                >
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            variant="contained"
                            bgcolor={(activeTab === 1) ? "var(--primary-color)" : "var(--disabled-grey-color)"}
                            disableElevation
                            startIcon={<PersonIcon />}
                            sx={{
                                width: { mobile: "100%" },
                                color: (activeTab === 1) ? "var(--light-color)" : "var(--primary-color)"
                            }}
                            onClick={(e) => handleActiveTabUpdate(e, 1)}
                        >
                            <Typography
                                variant={"button"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"inherit"}
                                textTransform={"inherit"}
                            >
                                Individual Account
                            </Typography>
                        </BaseButton>
                    </Box>
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            variant="contained"
                            bgcolor={(activeTab === 2) ? "var(--primary-color)" : "var(--disabled-grey-color)"}
                            disableElevation
                            startIcon={<GroupsIcon />}
                            sx={{
                                width: { mobile: "100%" },
                                color: (activeTab === 2) ? "var(--light-color)" : "var(--primary-color)"
                            }}
                            onClick={(e) => handleActiveTabUpdate(e, 2)}
                        >
                            <Typography
                                variant={"button"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"inherit"}
                                textTransform={"inherit"}
                            >
                                Group Account
                            </Typography>
                        </BaseButton>
                    </Box>
                </Stack>
                {activeTab === 1 && (
                    <OnboardCustomerAccount
                        error={error}
                        isLoading={isLoading}
                        handleSubmit={handleSubmit}
                        formDetails={customerDetails}
                        setFormDetails={setCustomerDetails}
                    />
                )}
                {activeTab === 2 && (
                    <OnboardGroupAccount
                        error={error}
                        isLoading={isLoading}
                        handleSubmit={handleSubmit}
                        formDetails={groupDetails}
                        setFormDetails={setGroupDetails}
                    />
                )}
            </CreateCustomerWrapper>
        </AppLayout >
    )
}